import { useState, KeyboardEvent } from 'react';
import { UserPlus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Modal } from './modal';
import { sendInvite } from '@/services/members/send-invite';
import { useValidateEmail } from '@/hooks/use-validate-email';
import { useNotification } from '@/components/notifications';
import { DialogClose } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import HelpTooltip from '@/components/ui/help-tooltip';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { LoadingSpinner } from '@/components/loading-spinner';

interface AddUserModalProps {
  orgId?: string | null;
  canChooseRole?: boolean;
  isOpen?: boolean;
  setOrgId?: (orgId: null) => void;
  setAddUserModalOpen: (isModalOpen: boolean) => void;
  onMemberAdded?: () => Promise<void>;
}

export const AddMemberModal = ({
  setOrgId,
  orgId,
  setAddUserModalOpen,
  canChooseRole = false,
  isOpen = false,
  onMemberAdded,
}: AddUserModalProps) => {
  const notification = useNotification();
  const [role, setRole] = useState('member');
  const [isLoading, setIsLoading] = useState(false);
  const { email, setEmail, isEmailValid } = useValidateEmail();

  const handleSendInvite = async () => {
    try {
      setIsLoading(true);
      const response = await sendInvite({
        email,
        role,
        canChooseRole,
        organizationId: orgId,
      });

      if (response?.detail) {
        notification.error(response.detail);
        return;
      }

      notification.success('Invite sent successfully');

      // Refresh the member list
      if (onMemberAdded) {
        await onMemberAdded();
      }

      setAddUserModalOpen(false);

      if (setOrgId) {
        setOrgId(null);
      }
    } catch (error) {
      console.error('Send invite error:', error);
      notification.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
      setEmail('');
      setRole('member');
      setAddUserModalOpen(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!email?.trim()) {
      return;
    }

    if (event.key === 'Enter') {
      handleSendInvite();
    }
  };

  const Title = () => (
    <div className='flex items-center gap-2'>
      <UserPlus />
      <span id='org-members-add-title'>Add member</span>
    </div>
  );

  const Footer = () => (
    <>
      <DialogClose asChild>
        <Button id='org-members-add-cancel' variant='outline'>
          Cancel
        </Button>
      </DialogClose>
      <Button
        id='org-members-add-submit'
        disabled={!email?.trim() || !isEmailValid || isLoading}
        onClick={handleSendInvite}
      >
        {isLoading ? (
          <>
            <LoadingSpinner /> Sending..
          </>
        ) : (
          `Send invite`
        )}
      </Button>
    </>
  );

  return (
    <Modal
      isDialogOpen={isOpen}
      setIsDialogOpen={(value) => {
        setRole('member');
        setEmail('');
        setAddUserModalOpen(value);
      }}
      title={<Title />}
      footer={<Footer />}
    >
      <div className='mt-2'>
        <div className='mb-3'>
          <Label htmlFor='email' className='block mb-2 text-sm font-medium'>
            <span className='inline-flex items-center gap-2'>
              <span>User email</span>
              <HelpTooltip label='User email help'>
                Send an invitation for a user to join your organization.
              </HelpTooltip>
            </span>
          </Label>
          <Input
            error={isEmailValid ? '' : 'Invalid email'}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            type='email'
            id='email'
            autoFocus
          />
        </div>
        {canChooseRole && (
          <RadioGroup value={role} onValueChange={setRole}>
            <div className='flex items-center space-x-2'>
              <RadioGroupItem value='owner' id='radio-button-owner' />
              <Label htmlFor='radio-button-owner'>Owner</Label>
            </div>
            <div className='flex items-center space-x-2'>
              <RadioGroupItem value='member' id='radio-button-member' />
              <Label htmlFor='radio-button-member'>Member</Label>
            </div>
          </RadioGroup>
        )}
      </div>
    </Modal>
  );
};
